import { SharePointListClient, odataEscape, type ListItem } from '../SharePointListClient';

/**
 * `02.DetalleOperaciones` — one row per line-item on a Venta/Cotización.
 *
 * SharePoint storage quirks confirmed from a live sample:
 *  - Amount + percentage fields come back as **strings** (`"5386000"`,
 *    `"10,5"`, `"30"`). Use `parseMoney()` at the read site, not
 *    `Number()` — the comma decimal would break `Number`.
 *  - The monetary/percentage columns were defined in SharePoint with a
 *    `_RO` suffix (matches the header list) while the discrete fields
 *    are `_DO`. Both are on the same list; we keep both here.
 *  - `Moneda_DO` isn't present on legacy rows — treat as optional.
 */
export type DetalleOperacionesFields = {
  Title?: string | null;
  IDOperacion_DO?: string | null;
  IDArticulo_DO?: string | null;
  ArticuloInventarioID_DO?: string | null;
  Articulo_DO?: string | null;
  Cliente_DO?: string | null;
  Status_DO?: 'Activo' | 'Pendiente Aprobacion' | 'Anulado' | string | null;
  EsRegalo_DO?: 'SI' | 'NO' | string | null;
  /** Garantía por línea (server/src/lib/garantias.ts). */
  LlevaGarantia_DO?: 'SI' | 'NO' | string | null;
  GarantiaMeses_DO?: string | null;
  /** 'SI' = vencimiento puesto a mano (el producto no tenía duración). */
  GarantiaManual_DO?: 'SI' | 'NO' | string | null;
  /** yyyy-mm-dd: entrega (se pone al despachar). */
  GarantiaDesde_DO?: string | null;
  /** yyyy-mm-dd: vencimiento. */
  GarantiaVence_DO?: string | null;
  Moneda_DO?: 'ARS' | 'MXN' | 'USD' | string | null;

  // Stored as strings; parse with `parseMoney`.
  Monto_RO?: string | number | null;
  Cantidad_RO?: string | number | null;
  Subtotal_RO?: string | number | null;
  SubtotalDescontado_DO?: string | number | null;
  Total_DO?: string | number | null;
  IVA_RO?: string | null;
  Descuento_RO?: string | null;

  // Per-line despacho serialization. Populated when admin confirms the
  // dispatch on a venta with `LlevaNroSerie_ART = SI` articles. Live
  // backfill reads these; declaring them here keeps client typings
  // honest with the server-side reads.
  NroSerie_DO?: string | null;
  NroDespacho_DO?: string | null;
};

export type DetalleOperacionesItem = ListItem<DetalleOperacionesFields>;

export const detalleOperacionesClient = new SharePointListClient<DetalleOperacionesFields>(
  'DetalleOperaciones',
);

/** Load every line attached to a given header ID. */
export async function listDetallesByOperacion(
  operacionId: string | number,
): Promise<DetalleOperacionesItem[]> {
  // `fields/Created` isn't indexed on this list, so Graph refuses it as an
  // orderBy. List-item `id` is assigned monotonically so sorting by it
  // preserves insertion order — we do that client-side after the fetch.
  const items = await detalleOperacionesClient.listItems({
    filter: `fields/IDOperacion_DO eq ${odataEscape(String(operacionId))}`,
  });
  return items.sort((a, b) => Number(a.id) - Number(b.id));
}
